import React, { useMemo, useState } from 'react';
import EventBrellaLayout from './EventBrellaLayout';
import { useTalkToUs } from './TalkToUsPopup';

const PLATFORM_RATE = 0.0179;
const FLAT_FEE = 1.19;
const PARTNER_PLATFORM_SHARE = 0.40;
const PARTNER_FLAT_SHARE = 0.50;

const FEE_ROWS = [
  { price: '$25', perTicket: '$1.64', perHundred: '$163.75' },
  { price: '$50', perTicket: '$2.09', perHundred: '$208.50' },
  { price: '$100', perTicket: '$2.98', perHundred: '$298.00' },
];

const PARTNER_ROWS = [
  { price: '$25', perTicket: '$0.77', perHundred: '$77.40' },
  { price: '$50', perTicket: '$0.95', perHundred: '$95.30' },
  { price: '$100', perTicket: '$1.31', perHundred: '$131.10' },
];

function feePerTicket(price) {
  return price * PLATFORM_RATE + FLAT_FEE;
}

function partnerEarningsPerTicket(price) {
  return price * PLATFORM_RATE * PARTNER_PLATFORM_SHARE + FLAT_FEE * PARTNER_FLAT_SHARE;
}

function money(value) {
  if (!Number.isFinite(value)) return '$0.00';
  return `$${value.toFixed(2)}`;
}

function parsePositiveNumber(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

const FeesTable = ({ columns, rows, footerLeft, footerRight }) => (
  <div className="fees-scroll">
    <div className="ledger-card fees-ledger">
      <div className="ledger-head fees-ledger-head">
        <span className="tag a">{columns[0]}</span>
        <span className="tag a">{columns[1]}</span>
        <span className="tag b">{columns[2]}</span>
      </div>
      {rows.map((row) => (
        <div className="ledger-row" key={row.price}>
          <div className="label">{row.price}</div>
          <div className="val">{row.perTicket}</div>
          <div className="val eb-new">{row.perHundred}</div>
        </div>
      ))}
      <div className="ledger-foot">
        <span>{footerLeft}</span>
        <strong>{footerRight}</strong>
      </div>
    </div>
  </div>
);

const FeesCalculator = () => {
  const [ticketPrice, setTicketPrice] = useState('50');
  const [ticketCount, setTicketCount] = useState('100');

  const results = useMemo(() => {
    const price = parsePositiveNumber(ticketPrice);
    const count = parsePositiveNumber(ticketCount);
    const fee = feePerTicket(price);
    const partner = partnerEarningsPerTicket(price);
    return {
      feePerTicket: money(fee),
      feeTotal: money(fee * count),
      partnerPerTicket: money(partner),
      partnerTotal: money(partner * count),
    };
  }, [ticketPrice, ticketCount]);

  return (
    <div className="ledger-card fees-calc-card">
      <div className="fees-calc-grid">
        <label className="eb-talk-field">
          <span>Ticket price</span>
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={ticketPrice}
            onChange={(e) => setTicketPrice(e.target.value)}
            aria-label="Ticket price"
          />
        </label>
        <label className="eb-talk-field">
          <span>Ticket count</span>
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={ticketCount}
            onChange={(e) => setTicketCount(e.target.value)}
            aria-label="Ticket count"
          />
        </label>
      </div>
      <div className="fees-calc-results">
        <div className="fees-calc-stat">
          <span>Fee per ticket</span>
          <strong>{results.feePerTicket}</strong>
        </div>
        <div className="fees-calc-stat">
          <span>Fee owed</span>
          <strong>{results.feeTotal}</strong>
        </div>
        <div className="fees-calc-stat">
          <span>You earn per ticket</span>
          <strong>{results.partnerPerTicket}</strong>
        </div>
        <div className="fees-calc-stat">
          <span>Partner earnings</span>
          <strong>{results.partnerTotal}</strong>
        </div>
      </div>
    </div>
  );
};

const EventBrellaFeesInner = () => {
  const { openTalkToUs } = useTalkToUs();

  return (
    <section className="ledger-section">
      <div className="wrap">
        <div className="fees-block">
          <div className="section-head">
            <span className="eyebrow">The Fees</span>
            <h2>Simple pricing. No surprises.</h2>
            <p className="sub">One flat rate. No monthly fee. No hidden markups.</p>
          </div>
          <FeesTable
            columns={['Ticket Price', 'Fee per Ticket', 'Fee for 100 Tickets']}
            rows={FEE_ROWS}
            footerLeft="No monthly fee. Ever."
            footerRight="1.79% + $1.19 per ticket"
          />
          <p className="fees-caption">
            Fee = 1.79% of ticket price + $1.19 flat, per ticket. Applied at checkout.
          </p>
        </div>

        <div className="fees-block">
          <div className="section-head">
            <span className="eyebrow">Bring Your Own Events</span>
            <h3>Earn on every ticket you bring in.</h3>
            <p className="sub">
              Refer an event and earn 40% of our 1.79% platform fee plus 50% of the $1.19 per-ticket fee — for the life of that event.
            </p>
          </div>
          <FeesTable
            columns={['Ticket Price', 'You Earn per Ticket', 'You Earn per 100 Tickets']}
            rows={PARTNER_ROWS}
            footerLeft="Paid automatically, no invoicing"
            footerRight="40% of platform fee + 50% of per-ticket fee"
          />
        </div>

        <div className="fees-block">
          <div className="section-head">
            <span className="eyebrow">Your Numbers</span>
            <h3>Run it on your ticket price.</h3>
            <p className="sub">No monthly fee. Update the price or count and the math updates with you.</p>
          </div>
          <FeesCalculator />
        </div>

        <div className="ledger-cta">
          <button type="button" className="btn btn-gold" onClick={openTalkToUs}>
            Talk to Us About Partnering
          </button>
        </div>
      </div>
    </section>
  );
};

const EventBrellaFees = () => (
  <EventBrellaLayout>
    <EventBrellaFeesInner />
  </EventBrellaLayout>
);

export default EventBrellaFees;
